import { ProductTypes } from "../../productCommons/index.js";

type WeightRecord = {
  containerType: string;
  unit: string;
  volume: number;
  packQuantity?: number;
  weight: number;
};

type Item = {
  containerType: string | null;
  unit: string | null;
  volume: number | null;
  packQuantity: number | null;
  weight: number | null;
};

const normalizeVolume = (volume: number): number => {
  return Math.round(volume * 10) / 10;
};

// Units like "cl" (centilitre), "pt" (pint), and "oz" (fluid ounce) are
// deliberately NOT converted to ml here — cl-denominated products are
// matched directly against their own cl-keyed table entries, rather than
// being converted into ml, since a cl volume with no exact entry should
// report as a genuine miss rather than silently resolve through an
// ml-keyed record it doesn't actually correspond to.
const normalizeUnitAndVolume = (
  unit: string | null,
  volume: number | null,
): { unit: string; volume: number } | null => {
  if (!unit || volume == null) return null;

  const normalized = unit.replace(/\s+/g, "").toLowerCase();

  if (["l", "lt", "liter", "litre"].includes(normalized)) {
    return { unit: "L", volume: normalizeVolume(volume) };
  }
  if (normalized === "ml") {
    return { unit: "ml", volume: normalizeVolume(volume) };
  }
  // "g" is bucketed together with "gal"/"gallon"/"gallons" here, even though
  // it reads like the abbreviation for grams (a weight unit, not a volume
  // one). This is intentional, not a bug: some live production data uses a
  // bare "g" to mean gallon (mirroring the "G" used for cask fallback units,
  // see draughtFallbackWeight below), so removing this mapping silently
  // breaks those existing records. If a genuinely gram-denominated item ever
  // reaches this function with unit "g", it will incorrectly be treated as a
  // gallon — but in practice such items should already carry an explicit
  // `weight`, which short-circuits before this function is ever called (see
  // getItemWeightWithMeta's `if (item.weight) return ...` at the top).
  if (["g", "gal", "gallon", "gallons"].includes(normalized)) {
    return { unit: "gal", volume: normalizeVolume(volume) };
  }

  return { unit: normalized, volume: normalizeVolume(volume) };
};

const buildWeightKey = (
  containerType: string,
  unit: string,
  volume: number,
  packQuantity?: number | null,
): string => {
  const parts: (string | number)[] = [containerType, unit, volume];
  if (packQuantity && packQuantity > 1) parts.push(packQuantity);
  return parts.join("|");
};

const canonicalizeContainerType = (containerType: string): string | null => {
  const trimmed = containerType.trim();
  if (!trimmed) return null;

  const casks = ProductTypes.beverage.children.draught.children.cask.children;
  const kegs = ProductTypes.beverage.children.draught.children.keg.children;

  if (trimmed in casks) return "cask";
  if (trimmed in kegs) {
    return kegs[trimmed as keyof typeof kegs].materialType?.trim() ?? null;
  }

  return trimmed;
};

const draughtFallbackWeight = (
  containerType: string,
  unit: string,
  volume: number,
): number => {
  if (containerType === "cask") {
    if (["G", "gal"].includes(unit)) {
      if (volume <= 4.5) return 23.25;
      if (volume <= 9) return 47.5;
    }
    if (unit === "L") return 47.5;
  }

  if (["plasticKeg", "steelKeg"].includes(containerType)) {
    if (unit === "L") {
      if (volume <= 10) return 17;
      if (volume <= 15) return 24;
      if (volume <= 20) return 32;
      if (volume <= 30) return 40;
      if (volume <= 50) return 63;
      if (volume <= 60) return 75;
    }
  }

  return 0;
};

const DEFAULT_WEIGHT_RECORDS: WeightRecord[] = [
  // Plastic kegs
  { containerType: "plasticKeg", unit: "L", volume: 10, weight: 11 },
  { containerType: "plasticKeg", unit: "L", volume: 20, weight: 21 },
  { containerType: "plasticKeg", unit: "L", volume: 30, weight: 30 },

  // Steel kegs
  { containerType: "steelKeg", unit: "L", volume: 10, weight: 17 },
  { containerType: "steelKeg", unit: "L", volume: 12, weight: 15 },
  { containerType: "steelKeg", unit: "L", volume: 15, weight: 20.25 },
  { containerType: "steelKeg", unit: "L", volume: 15.5, weight: 24 },
  { containerType: "steelKeg", unit: "L", volume: 19.5, weight: 27 },
  { containerType: "steelKeg", unit: "L", volume: 20, weight: 32 },
  { containerType: "steelKeg", unit: "L", volume: 25, weight: 33 },
  { containerType: "steelKeg", unit: "L", volume: 29.3, weight: 38 },
  { containerType: "steelKeg", unit: "L", volume: 30, weight: 40 },
  { containerType: "steelKeg", unit: "L", volume: 50, weight: 63 },
  { containerType: "steelKeg", unit: "L", volume: 58.7, weight: 75 },
  { containerType: "steelKeg", unit: "pt", volume: 32, weight: 25 },
  { containerType: "steelKeg", unit: "pt", volume: 72, weight: 50 },

  // Dolium kegs
  { containerType: "doliumKeg", unit: "L", volume: 20, weight: 25 },
  { containerType: "doliumKeg", unit: "L", volume: 30, weight: 35 },

  // Eco kegs
  { containerType: "ecoKeg", unit: "L", volume: 30, weight: 34.75 },

  // Key kegs
  { containerType: "keyKeg", unit: "L", volume: 10, weight: 12.25 },
  { containerType: "keyKeg", unit: "L", volume: 20, weight: 25.25 },
  { containerType: "keyKeg", unit: "L", volume: 30, weight: 33.75 },

  // Mini kegs
  { containerType: "miniKeg", unit: "L", volume: 5, weight: 6 },
  { containerType: "miniKeg", unit: "L", volume: 10, weight: 14 },
  {
    containerType: "miniKeg",
    unit: "L",
    volume: 5,
    packQuantity: 4,
    weight: 24,
  },

  // Petainer kegs
  { containerType: "petainerKeg", unit: "L", volume: 19.5, weight: 21 },
  { containerType: "petainerKeg", unit: "L", volume: 20, weight: 22 },
  { containerType: "petainerKeg", unit: "L", volume: 30, weight: 33 },

  // Poly kegs
  { containerType: "polyKeg", unit: "L", volume: 12, weight: 17 },
  { containerType: "polyKeg", unit: "L", volume: 20, weight: 25 },
  { containerType: "polyKeg", unit: "L", volume: 30, weight: 34.25 },

  // S-type kegstar
  { containerType: "sTypeKegstar", unit: "L", volume: 30, weight: 40 },

  // Casks
  { containerType: "cask", unit: "G", volume: 4.5, weight: 23.25 },
  { containerType: "cask", unit: "G", volume: 9, weight: 47.5 },
  { containerType: "cask", unit: "gal", volume: 4.5, weight: 23.25 },
  { containerType: "cask", unit: "gal", volume: 9, weight: 47.5 },
  { containerType: "cask", unit: "L", volume: 40, weight: 47.5 },
  { containerType: "cask", unit: "L", volume: 40.9, weight: 47.5 },
  { containerType: "cask", unit: "L", volume: 41, weight: 47.5 },

  // Bag in box
  { containerType: "bagInBox", unit: "L", volume: 3, weight: 3.5 },
  { containerType: "bagInBox", unit: "L", volume: 5, weight: 6.5 },
  { containerType: "bagInBox", unit: "L", volume: 10, weight: 12.25 },
  { containerType: "bagInBox", unit: "L", volume: 20, weight: 25 },

  // Bottles
  { containerType: "bottle", unit: "cl", volume: 70, weight: 1.5 },
  {
    containerType: "bottle",
    unit: "cl",
    volume: 75,
    weight: 1.58,
  },
  {
    containerType: "bottle",
    unit: "cl",
    volume: 75,
    packQuantity: 6,
    weight: 9.5,
  },
  {
    containerType: "bottle",
    unit: "cl",
    volume: 75,
    packQuantity: 12,
    weight: 17.25,
  },
  {
    containerType: "bottle",
    unit: "cl",
    volume: 70,
    packQuantity: 6,
    weight: 9.5,
  },
  {
    containerType: "bottle",
    unit: "cl",
    volume: 70,
    packQuantity: 12,
    weight: 17.25,
  },
  { containerType: "bottle", unit: "ml", volume: 1500, weight: 3 },
  {
    containerType: "bottle",
    unit: "ml",
    volume: 125,
    packQuantity: 24,
    weight: 7,
  },
  {
    containerType: "bottle",
    unit: "ml",
    volume: 200,
    packQuantity: 24,
    weight: 9,
  },
  {
    containerType: "bottle",
    unit: "ml",
    volume: 250,
    packQuantity: 12,
    weight: 7.75,
  },
  {
    containerType: "bottle",
    unit: "ml",
    volume: 250,
    packQuantity: 20,
    weight: 8,
  },
  {
    containerType: "bottle",
    unit: "ml",
    volume: 250,
    packQuantity: 24,
    weight: 13,
  },
  {
    containerType: "bottle",
    unit: "ml",
    volume: 275,
    packQuantity: 12,
    weight: 7.25,
  },
  {
    containerType: "bottle",
    unit: "ml",
    volume: 275,
    packQuantity: 24,
    weight: 15.25,
  },
  {
    containerType: "bottle",
    unit: "ml",
    volume: 300,
    packQuantity: 12,
    weight: 8,
  },
  {
    containerType: "bottle",
    unit: "ml",
    volume: 300,
    packQuantity: 24,
    weight: 15.5,
  },
  { containerType: "bottle", unit: "ml", volume: 330, weight: 0.5 },
  {
    containerType: "bottle",
    unit: "ml",
    volume: 330,
    packQuantity: 4,
    weight: 3,
  },
  {
    containerType: "bottle",
    unit: "ml",
    volume: 330,
    packQuantity: 6,
    weight: 4.25,
  },
  {
    containerType: "bottle",
    unit: "ml",
    volume: 330,
    packQuantity: 8,
    weight: 10,
  },
  {
    containerType: "bottle",
    unit: "ml",
    volume: 330,
    packQuantity: 12,
    weight: 8,
  },
  {
    containerType: "bottle",
    unit: "ml",
    volume: 330,
    packQuantity: 15,
    weight: 10,
  },
  {
    containerType: "bottle",
    unit: "ml",
    volume: 330,
    packQuantity: 18,
    weight: 10,
  },
  {
    containerType: "bottle",
    unit: "ml",
    volume: 330,
    packQuantity: 20,
    weight: 13.5,
  },
  {
    containerType: "bottle",
    unit: "ml",
    volume: 330,
    packQuantity: 24,
    weight: 15.75,
  },
  { containerType: "bottle", unit: "ml", volume: 350, weight: 1 },
  { containerType: "bottle", unit: "ml", volume: 355, weight: 0.75 },
  {
    containerType: "bottle",
    unit: "ml",
    volume: 355,
    packQuantity: 6,
    weight: 3.75,
  },
  {
    containerType: "bottle",
    unit: "ml",
    volume: 355,
    packQuantity: 12,
    weight: 10,
  },
  {
    containerType: "bottle",
    unit: "ml",
    volume: 355,
    packQuantity: 24,
    weight: 14.25,
  },
  { containerType: "bottle", unit: "ml", volume: 375, weight: 0.5 },
  {
    containerType: "bottle",
    unit: "ml",
    volume: 375,
    packQuantity: 4,
    weight: 5,
  },
  {
    containerType: "bottle",
    unit: "ml",
    volume: 375,
    packQuantity: 6,
    weight: 5.5,
  },
  {
    containerType: "bottle",
    unit: "ml",
    volume: 375,
    packQuantity: 8,
    weight: 7,
  },
  {
    containerType: "bottle",
    unit: "ml",
    volume: 375,
    packQuantity: 12,
    weight: 11,
  },
  {
    containerType: "bottle",
    unit: "ml",
    volume: 375,
    packQuantity: 15,
    weight: 12,
  },
  {
    containerType: "bottle",
    unit: "ml",
    volume: 375,
    packQuantity: 20,
    weight: 16,
  },
  {
    containerType: "bottle",
    unit: "ml",
    volume: 375,
    packQuantity: 24,
    weight: 18.5,
  },
  { containerType: "bottle", unit: "ml", volume: 500, weight: 1 },
  {
    containerType: "bottle",
    unit: "ml",
    volume: 500,
    packQuantity: 6,
    weight: 5,
  },
  {
    containerType: "bottle",
    unit: "ml",
    volume: 500,
    packQuantity: 8,
    weight: 8,
  },
  {
    containerType: "bottle",
    unit: "ml",
    volume: 500,
    packQuantity: 10,
    weight: 10.25,
  },
  {
    containerType: "bottle",
    unit: "ml",
    volume: 500,
    packQuantity: 12,
    weight: 11.75,
  },
  {
    containerType: "bottle",
    unit: "ml",
    volume: 500,
    packQuantity: 16,
    weight: 13,
  },
  {
    containerType: "bottle",
    unit: "ml",
    volume: 500,
    packQuantity: 18,
    weight: 19,
  },
  {
    containerType: "bottle",
    unit: "ml",
    volume: 500,
    packQuantity: 20,
    weight: 19.75,
  },
  {
    containerType: "bottle",
    unit: "ml",
    volume: 550,
    packQuantity: 12,
    weight: 11.75,
  },
  {
    containerType: "bottle",
    unit: "ml",
    volume: 650,
    packQuantity: 12,
    weight: 14.5,
  },
  {
    containerType: "bottle",
    unit: "ml",
    volume: 660,
    packQuantity: 6,
    weight: 10,
  },
  {
    containerType: "bottle",
    unit: "ml",
    volume: 660,
    packQuantity: 12,
    weight: 16.5,
  },
  { containerType: "bottle", unit: "ml", volume: 700, weight: 1.5 },
  { containerType: "bottle", unit: "ml", volume: 750, weight: 2 },
  {
    containerType: "bottle",
    unit: "ml",
    volume: 750,
    packQuantity: 3,
    weight: 5,
  },
  {
    containerType: "bottle",
    unit: "ml",
    volume: 750,
    packQuantity: 6,
    weight: 9.5,
  },
  {
    containerType: "bottle",
    unit: "ml",
    volume: 750,
    packQuantity: 12,
    weight: 17.25,
  },
  {
    containerType: "bottle",
    unit: "ml",
    volume: 768,
    packQuantity: 6,
    weight: 8.25,
  },
  {
    containerType: "bottle",
    unit: "ml",
    volume: 768,
    packQuantity: 12,
    weight: 17.5,
  },
  {
    containerType: "bottle",
    unit: "L",
    volume: 1,
    packQuantity: 6,
    weight: 7.5,
  },
  {
    containerType: "bottle",
    unit: "L",
    volume: 1,
    packQuantity: 12,
    weight: 15,
  },
  { containerType: "bottle", unit: "L", volume: 1, weight: 1.75 },
  { containerType: "bottle", unit: "L", volume: 1.5, weight: 2.75 },
  { containerType: "bottle", unit: "L", volume: 2, weight: 4 },
  { containerType: "bottle", unit: "L", volume: 3, weight: 5.75 },
  { containerType: "bottle", unit: "L", volume: 6, weight: 10 },
  { containerType: "bottle", unit: "L", volume: 9, weight: 13.75 },

  // Cans
  {
    containerType: "can",
    unit: "ml",
    volume: 100,
    packQuantity: 12,
    weight: 2,
  },
  {
    containerType: "can",
    unit: "ml",
    volume: 125,
    packQuantity: 12,
    weight: 1.75,
  },
  {
    containerType: "can",
    unit: "ml",
    volume: 125,
    packQuantity: 24,
    weight: 3.5,
  },
  {
    containerType: "can",
    unit: "ml",
    volume: 140,
    packQuantity: 12,
    weight: 2,
  },
  {
    containerType: "can",
    unit: "ml",
    volume: 250,
    packQuantity: 12,
    weight: 7.25,
  },
  {
    containerType: "can",
    unit: "ml",
    volume: 250,
    packQuantity: 24,
    weight: 7.75,
  },
  {
    containerType: "can",
    unit: "ml",
    volume: 330,
    packQuantity: 8,
    weight: 8.25,
  },
  {
    containerType: "can",
    unit: "ml",
    volume: 330,
    packQuantity: 12,
    weight: 5,
  },
  {
    containerType: "can",
    unit: "ml",
    volume: 330,
    packQuantity: 16,
    weight: 6,
  },
  {
    containerType: "can",
    unit: "ml",
    volume: 330,
    packQuantity: 18,
    weight: 6.5,
  },
  {
    containerType: "can",
    unit: "ml",
    volume: 330,
    packQuantity: 20,
    weight: 7.75,
  },
  {
    containerType: "can",
    unit: "ml",
    volume: 330,
    packQuantity: 24,
    weight: 9.5,
  },
  {
    containerType: "can",
    unit: "ml",
    volume: 355,
    packQuantity: 4,
    weight: 1.5,
  },
  {
    containerType: "can",
    unit: "ml",
    volume: 355,
    packQuantity: 6,
    weight: 2.5,
  },
  {
    containerType: "can",
    unit: "ml",
    volume: 355,
    packQuantity: 12,
    weight: 8.5,
  },
  {
    containerType: "can",
    unit: "ml",
    volume: 355,
    packQuantity: 15,
    weight: 5.5,
  },
  {
    containerType: "can",
    unit: "ml",
    volume: 355,
    packQuantity: 24,
    weight: 10,
  },
  {
    containerType: "can",
    unit: "ml",
    volume: 375,
    packQuantity: 24,
    weight: 6.75,
  },
  {
    containerType: "can",
    unit: "ml",
    volume: 440,
    packQuantity: 12,
    weight: 7,
  },
  {
    containerType: "can",
    unit: "ml",
    volume: 440,
    packQuantity: 16,
    weight: 14.5,
  },
  {
    containerType: "can",
    unit: "ml",
    volume: 440,
    packQuantity: 18,
    weight: 9,
  },
  {
    containerType: "can",
    unit: "ml",
    volume: 440,
    packQuantity: 24,
    weight: 14.5,
  },
  {
    containerType: "can",
    unit: "ml",
    volume: 473,
    packQuantity: 12,
    weight: 8.5,
  },
  {
    containerType: "can",
    unit: "ml",
    volume: 473,
    packQuantity: 24,
    weight: 13.25,
  },
  {
    containerType: "can",
    unit: "ml",
    volume: 500,
    packQuantity: 12,
    weight: 7.25,
  },
  {
    containerType: "can",
    unit: "ml",
    volume: 500,
    packQuantity: 15,
    weight: 9,
  },
  {
    containerType: "can",
    unit: "ml",
    volume: 500,
    packQuantity: 18,
    weight: 10,
  },
  {
    containerType: "can",
    unit: "ml",
    volume: 500,
    packQuantity: 20,
    weight: 12,
  },
  {
    containerType: "can",
    unit: "ml",
    volume: 500,
    packQuantity: 24,
    weight: 13.25,
  },
  {
    containerType: "can",
    unit: "ml",
    volume: 568,
    packQuantity: 12,
    weight: 8,
  },
  {
    containerType: "can",
    unit: "ml",
    volume: 568,
    packQuantity: 24,
    weight: 16,
  },
];

const DEFAULT_WEIGHTS = new Map<string, number>(
  DEFAULT_WEIGHT_RECORDS.map((record) => [
    buildWeightKey(
      record.containerType,
      record.unit,
      record.volume,
      record.packQuantity,
    ),
    record.weight,
  ]),
);

const DRAUGHT_CONTAINER_TYPES = ["cask", "plasticKeg", "steelKeg"];

export enum WeightMatchType {
  /**
   * The caller already supplied an explicit item weight — not looked up
   * against the table at all. Trusted as-is
   * regardless of what the table would otherwise resolve to.
   */
  PROVIDED = "PROVIDED",
  /** An exact per-SKU/per-volume table entry was found. */
  DIRECT_MATCH = "DIRECT_MATCH",
  /**
   * No exact table entry — resolved via draughtFallbackWeight's bucketed
   * formula (e.g. "any steel keg <=30L weighs 40kg") instead of a verified
   * per-SKU weight. Usable, but coarser than a direct match, and a good
   * candidate for being replaced by a concrete table entry over time.
   */
  DRAUGHT_FALLBACK = "DRAUGHT_FALLBACK",
  /** Nothing matched; weight is 0 and should not be trusted. */
  NO_MATCH = "NO_MATCH",
}

type WeightResult = { weight: number; matchType: WeightMatchType };

export type WeightLookupLogger = {
  warn: (message: string) => void;
};

const getSingleWeight = (
  originalContainerType: string,
  genericContainerType: string,
  unit: string,
  volume: number,
): WeightResult => {
  const specificSingleWeight = DEFAULT_WEIGHTS.get(
    buildWeightKey(originalContainerType, unit, volume),
  );
  if (specificSingleWeight != null) {
    return {
      weight: specificSingleWeight,
      matchType: WeightMatchType.DIRECT_MATCH,
    };
  }

  const genericSingleWeight = DEFAULT_WEIGHTS.get(
    buildWeightKey(genericContainerType, unit, volume),
  );
  if (genericSingleWeight != null) {
    return {
      weight: genericSingleWeight,
      matchType: WeightMatchType.DIRECT_MATCH,
    };
  }

  if (DRAUGHT_CONTAINER_TYPES.includes(genericContainerType)) {
    const fallbackWeight = draughtFallbackWeight(
      genericContainerType,
      unit,
      volume,
    );
    return fallbackWeight > 0
      ? { weight: fallbackWeight, matchType: WeightMatchType.DRAUGHT_FALLBACK }
      : { weight: 0, matchType: WeightMatchType.NO_MATCH };
  }

  return { weight: 0, matchType: WeightMatchType.NO_MATCH };
};

/**
 * Like getItemWeight, but also reports how the weight was resolved — see
 * WeightMatchType for what each outcome means.
 *
 * Supplier-entered weight always wins: if `item.weight` is provided, it is
 * returned as-is and the canonical table is never consulted. Couriers charge
 * and penalise against the declared weight, and a supplier-specific weight
 * can legitimately differ from Sellar's typical estimate, so replacing it
 * with the canonical lookup could understate a shipment. The canonical table
 * is only used as a fallback when no supplier weight was provided.
 *
 * If `logger` is provided, a warning is logged for NO_MATCH and
 * DRAUGHT_FALLBACK results where containerType, unit, and volume were all
 * present (i.e. there was enough data to attempt a real lookup) — a product
 * with no container properties configured at all is a separate, likely
 * noisier, data-completeness issue and is not logged here. DRAUGHT_FALLBACK
 * is logged too (not just NO_MATCH) so these bucketed-formula guesses show
 * up as candidates for being replaced with concrete table entries.
 */
export const getItemWeightWithMeta = (
  item: Item,
  logger?: WeightLookupLogger,
): WeightResult => {
  if (item.weight) {
    return { weight: item.weight, matchType: WeightMatchType.PROVIDED };
  }

  const originalContainerType = item.containerType?.trim();

  if (!originalContainerType) {
    return { weight: 0, matchType: WeightMatchType.NO_MATCH };
  }

  const genericContainerType = canonicalizeContainerType(originalContainerType);

  if (!genericContainerType) {
    return { weight: 0, matchType: WeightMatchType.NO_MATCH };
  }

  const normalized = normalizeUnitAndVolume(item.unit, item.volume);

  if (!normalized) {
    return { weight: 0, matchType: WeightMatchType.NO_MATCH };
  }

  const { unit, volume } = normalized;

  const specificKey = buildWeightKey(
    originalContainerType,
    unit,
    volume,
    item.packQuantity,
  );

  const genericKey = buildWeightKey(
    genericContainerType,
    unit,
    volume,
    item.packQuantity,
  );

  const specificWeight = DEFAULT_WEIGHTS.get(specificKey);

  if (specificWeight != null) {
    return { weight: specificWeight, matchType: WeightMatchType.DIRECT_MATCH };
  }

  const genericWeight = DEFAULT_WEIGHTS.get(genericKey);

  if (genericWeight != null) {
    return { weight: genericWeight, matchType: WeightMatchType.DIRECT_MATCH };
  }

  const packQuantity = item.packQuantity ?? 1;

  const singleResult = getSingleWeight(
    originalContainerType,
    genericContainerType,
    unit,
    volume,
  );

  const result: WeightResult =
    packQuantity > 1
      ? {
          weight: singleResult.weight * packQuantity,
          matchType: singleResult.matchType,
        }
      : singleResult;

  if (
    result.matchType === WeightMatchType.NO_MATCH ||
    result.matchType === WeightMatchType.DRAUGHT_FALLBACK
  ) {
    logger?.warn(
      `getItemWeight: ${result.matchType} for containerType="${originalContainerType}" unit="${unit}" volume=${volume} packQuantity=${item.packQuantity ?? "null"}`,
    );
  }

  return result;
};

export const getItemWeight = (
  item: Item,
  logger?: WeightLookupLogger,
): number => getItemWeightWithMeta(item, logger).weight;
